import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import type {
  FieldData,
  FilterItem,
  FilterPinItem,
} from "../../FilterManagement/CommonFilterTypes";
import type { FilterEntity } from "../../FilterManagement/filterEntity";

export interface FilterBuilderState {
  fieldsData: FieldData[];
  context: string | null;
  currentFilter: FilterItem | null;
  currentFilterField?: string | null;
  onFilterSelect: (filter: FilterItem | FilterItem[]) => void;
  onCancel: () => void;
  // TODO: statusesData is typed as any[] across FilterBuilder siblings (FilterChipButton, FilterChip) — narrow once a shared status type exists.
  statusesData: any[];
  namePrefix: string;
  filtersToPin: FilterPinItem[];
  entity: FilterEntity | null;
}

export interface FilterBuilderContextValue {
  filterBuilderState: FilterBuilderState;
  updateFilterBuilderState: (newState: Partial<FilterBuilderState>) => void;
  selectFilterField: (field: string | null) => void;
}

export interface FilterBuilderProviderProps {
  children: ReactNode;
  onFilterSelect: (filter: FilterItem | FilterItem[]) => void;
  onCancel: () => void;
  currentFilter?: FilterItem | null;
  fieldsData: FieldData[];
  context: string | null;
  statusesData?: any[];
  filtersToPin?: FilterPinItem[];
  entity?: FilterEntity | null;
}

const initialState: FilterBuilderState = {
  fieldsData: [],
  context: null,
  currentFilter: null,
  onFilterSelect: () => {},
  onCancel: () => {},
  statusesData: [],
  namePrefix: "",
  filtersToPin: [],
  entity: null,
};

const initialContextValue: FilterBuilderContextValue = {
  filterBuilderState: initialState,
  updateFilterBuilderState: () => {},
  selectFilterField: () => {},
};

const filterBuilderContext =
  createContext<FilterBuilderContextValue>(initialContextValue);

const FilterBuilderProvider = ({
  children,
  onFilterSelect,
  onCancel,
  currentFilter,
  fieldsData,
  context,
  statusesData,
  filtersToPin,
  entity,
}: FilterBuilderProviderProps) => {
  const [filterBuilderState, updateFilterBuilderState] = useReducer(
    (state: FilterBuilderState, newState: Partial<FilterBuilderState>) => ({
      ...state,
      ...newState,
    }),
    // Cast to FilterBuilderState: the provider props mark statusesData,
    // filtersToPin and entity as optional, but the reducer's state type
    // requires them. Runtime is unchanged — the same values are forwarded.
    {
      fieldsData,
      context,
      currentFilter,
      currentFilterField: currentFilter?.field,
      onFilterSelect,
      onCancel,
      statusesData,
      namePrefix: "",
      filtersToPin,
      entity,
    } as FilterBuilderState
  );

  const selectFilterField = useCallback((field: string | null) => {
    updateFilterBuilderState({
      currentFilterField: field,
    });
  }, []);

  const filterBuilderContextValue = useMemo(
    () => ({
      filterBuilderState,
      updateFilterBuilderState,
      selectFilterField,
    }),
    [filterBuilderState]
  );

  return (
    <filterBuilderContext.Provider value={filterBuilderContextValue}>
      {children}
    </filterBuilderContext.Provider>
  );
};

export default FilterBuilderProvider;

export const useFilterBuilderContext = () => useContext(filterBuilderContext);
